import React from 'react';
import { Box, Stack, Skeleton } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

interface CommentSkeletonProps {
    showReplies?: boolean;
    depth?: number;
}

export const CommentSkeleton: React.FC<CommentSkeletonProps> = ({ showReplies = false, depth = 0 }) => {
    const { themeStyle } = useThemeContext();

    const SkeletonBubble = () => (
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ width: '100%', ml: depth > 0 ? 4 : 0 }}>
            {/* Avatar skeleton */}
            <Skeleton
                variant="circular"
                width={32}
                height={32}
                sx={{
                    mt: 0.5,
                    bgcolor: theme[themeStyle].form_gray,
                }}
            />

            {/* Comment bubble skeleton */}
            <Box
                flex={1}
                sx={{
                    background: theme[themeStyle].deep_grey,
                    borderRadius: 2,
                    p: 1,
                    minWidth: 0,
                }}>
                {/* Username skeleton */}
                <Skeleton
                    variant="text"
                    width="30%"
                    height={20}
                    sx={{
                        bgcolor: theme[themeStyle].form_gray,
                        mb: 0.5,
                    }}
                />

                {/* Comment text skeleton - 2 lines */}
                <Skeleton
                    variant="text"
                    width="90%"
                    height={16}
                    sx={{
                        bgcolor: theme[themeStyle].form_gray,
                        mb: 0.25,
                    }}
                />
                <Skeleton
                    variant="text"
                    width="65%"
                    height={16}
                    sx={{
                        bgcolor: theme[themeStyle].form_gray,
                        mb: 1,
                    }}
                />

                {/* Action buttons skeleton */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: theme[themeStyle].form_gray }} />
                    <Skeleton variant="text" width={20} height={14} sx={{ bgcolor: theme[themeStyle].form_gray }} />
                    <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: theme[themeStyle].form_gray }} />
                    <Skeleton variant="text" width={60} height={14} sx={{ bgcolor: theme[themeStyle].form_gray }} />
                </Stack>
            </Box>
        </Stack>
    );

    return (
        <Box sx={{ mb: 2 }}>
            <SkeletonBubble />

            {/* Show reply skeletons if needed */}
            {showReplies && (
                <Box sx={{ mt: 1 }}>
                    {/* Expand/collapse skeleton */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1, mb: 1 }}>
                        <Skeleton
                            variant="circular"
                            width={20}
                            height={20}
                            sx={{ bgcolor: theme[themeStyle].form_gray }}
                        />
                        <Skeleton variant="text" width={80} height={14} sx={{ bgcolor: theme[themeStyle].form_gray }} />
                    </Stack>

                    {/* Reply skeleton */}
                    <Box sx={{ ml: 4, position: 'relative' }}>
                        <SkeletonBubble />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export const CommentListSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => {
    return (
        <Box>
            {Array.from({ length: count }).map((_, index) => (
                <CommentSkeleton
                    key={index}
                    showReplies={index === 0} // Show replies skeleton only for first item
                />
            ))}
        </Box>
    );
};
